import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Package, AlertTriangle } from 'lucide-react';
import { T, PIE_COLORS } from './constants';

export const DashboardView = ({ occupiedBins, totalBins, occupancyRate, cabinetChartData, advancedStats, alerts, recentMovements }) => (
  <div className="flex-1 overflow-y-auto space-y-4 pr-2">
    <div className="grid grid-cols-3 gap-4">
      {/* Occupancy Pie */}
      <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: T.textMuted }}>Rata Ocupare</h3>
        <div className="flex items-center gap-4">
          <ResponsiveContainer width={100} height={100}>
            <PieChart><Pie data={[{ v: occupiedBins }, { v: totalBins - occupiedBins }]} dataKey="v" innerRadius={30} outerRadius={45} strokeWidth={0}>
              {PIE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
            </Pie></PieChart>
          </ResponsiveContainer>
          <div>
            <p className="text-3xl font-extrabold" style={{ color: T.success }}>{occupancyRate}%</p>
            <p className="text-xs" style={{ color: T.textDim }}>{occupiedBins} din {totalBins}</p>
          </div>
        </div>
      </div>
      {/* Cabinet Occupancy Chart */}
      <div className="col-span-2 rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: T.textMuted }}>Ocupare per Dulap</h3>
        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={cabinetChartData} barGap={2}>
            <XAxis dataKey="name" tick={{ fill: T.textDim, fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: T.textDim, fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
            <Tooltip contentStyle={{ background: T.paper, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 12 }} />
            <Bar dataKey="total" fill={T.subtle} radius={[4, 4, 0, 0]} name="Total" />
            <Bar dataKey="occupied" fill={T.success} radius={[4, 4, 0, 0]} name="Ocupate" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
    {/* Recent Stock */}
    <div className="grid grid-cols-2 gap-4">
      <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
        <h3 className="text-sm font-bold mb-3" style={{ color: T.textMuted }}>Top 10 Articole Stocate</h3>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {(advancedStats?.top_articles || []).map((a, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: T.subtle }}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold w-5 text-center" style={{ color: T.textDim }}>#{i+1}</span>
                <div><p className="text-xs font-medium">{a.name}</p><p className="text-[10px] font-mono" style={{ color: T.textDim }}>{a.code} &middot; {a.bins_count} locatii</p></div>
              </div>
              <span className="text-xs font-mono font-bold text-emerald-400">{a.total_qty}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: T.textMuted }}>
          <AlertTriangle className="w-4 h-4 text-orange-400" /> Alerte Stoc Minim
          {alerts.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 animate-pulse">{alerts.length}</span>}
        </h3>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {alerts.length === 0 ? (
            <p className="text-center text-xs py-4" style={{ color: T.textDim }}>Nicio alerta - totul OK</p>
          ) : alerts.map((a, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-lg border border-red-500/20" style={{ background: 'rgba(239,68,68,0.05)' }}>
              <div>
                <p className="text-xs font-mono font-medium">{a.code}</p>
                <p className="text-[10px]" style={{ color: T.textDim }}>{a.cabinet} &rarr; {a.shelf} &rarr; {a.position}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-mono"><span className="text-red-400">{a.current_stock}</span> / <span className="text-zinc-500">{a.min_stock}</span></p>
                <p className="text-[10px] text-red-400">-{a.deficit}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    {/* Stoc Activ */}
    <div className="rounded-xl border p-4" style={{ background: T.paper, borderColor: T.border }}>
      <h3 className="text-sm font-bold mb-3" style={{ color: T.textMuted }}>Stoc Activ</h3>
      <div className="space-y-2">
        {recentMovements.map((m, i) => (
          <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: T.subtle }}>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium">{m.article}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono" style={{ color: T.textDim }}>{m.bin}</span>
              <span className="text-xs font-mono font-bold text-emerald-400">{m.qty}</span>
            </div>
          </div>
        ))}
        {recentMovements.length === 0 && <p className="text-xs text-center py-4" style={{ color: T.textDim }}>Niciun stoc activ</p>}
      </div>
    </div>
  </div>
);
